/**
 * <SharedReportPage /> — the PUBLIC, read-only view of a shared report.
 *
 * Reachable at /r#<encoded-report> with NO login. It decodes the report from
 * the URL fragment and renders it client-side — see lib/share-report.ts and the
 * App.tsx short-circuit (which also skips the auth refresh on this route). It
 * makes ZERO funnel/API calls: nothing about the funnel is exposed, and the
 * report data never touches a server.
 *
 * It reuses the exact report surfaces (hero → process → shift → ROI) so the
 * shared page looks identical to what the rep showed — minus the input bar,
 * the Share button, and all app chrome. An Export PDF action is offered.
 */
import { useMemo, useState } from 'react';
import { decodeReport } from '../lib/share-report';
import MoxoLogo from '../components/sales-enablement/MoxoLogo';
import ReportHero, { type HeroEnrichment } from '../components/sales-enablement/ReportHero';
import ProcessSequence, { type ProcessStep } from '../components/sales-enablement/ProcessSequence';
import TransformationShift, {
  type Challenge,
  type MoxoSolution,
} from '../components/sales-enablement/TransformationShift';
import RoiCalculator, { type RoiCalculatorDefaults } from '../components/sales-enablement/RoiCalculator';
import ExecutiveBriefView from '../components/sales-enablement/ExecutiveBriefView';
import type { ExecutiveBriefResult } from '../components/sales-enablement/executive-brief-types';
import CallBriefView from '../components/sales-enablement/CallBriefView';
import type { CallBriefResult } from '../components/sales-enablement/call-brief-types';
import ProposalDeck from '../components/sales-pricing/ProposalDeck';
import { isPricingProposal, looksLikePricingProposal } from '../components/sales-pricing/proposal-types';

interface SharedReportBody {
  companySummary: string;
  industryLabel: string;
  processSteps: ProcessStep[];
  challenges: Challenge[];
  moxoSolutions: MoxoSolution[];
  lowSignal?: boolean;
}
interface SharedResult {
  ok?: boolean;
  kind?: string;
  processName?: string;
  companyName?: string | null;
  enrichment?: HeroEnrichment;
  report?: SharedReportBody | null;
  roiDefaults?: RoiCalculatorDefaults;
}

function isBrief(x: unknown): x is ExecutiveBriefResult {
  if (!x || typeof x !== 'object') return false;
  const r = x as ExecutiveBriefResult;
  return r.kind === 'executive_brief' && !!r.brief;
}

function looksLikeBrief(x: unknown): boolean {
  if (!x || typeof x !== 'object') return false;
  return (x as { kind?: string }).kind === 'executive_brief';
}

// The consolidated call_brief decode. Order-independent — it never changes how
// executive_brief or a kind-less discovery report decode.
function looksLikeCall(x: unknown): boolean {
  if (!x || typeof x !== 'object') return false;
  return (x as { kind?: string }).kind === 'call_brief';
}

function isCall(x: unknown): x is CallBriefResult {
  return looksLikeCall(x) && !!(x as CallBriefResult).brief;
}

function isValid(x: unknown): x is SharedResult {
  return !!x && typeof x === 'object' && !!(x as SharedResult).report;
}

export default function SharedReportPage() {
  const decoded = useMemo(() => decodeReport(window.location.hash), []);
  const proposalData = isPricingProposal(decoded) ? decoded : null;
  const callData = !proposalData && isCall(decoded) ? decoded : null;
  const briefData = !proposalData && !callData && isBrief(decoded) ? decoded : null;
  const data = !proposalData && !callData && !briefData && isValid(decoded) ? decoded : null;
  const [copied, setCopied] = useState(false);
  // "Back" is shown ONLY when the rep opened this to present (Present adds
  // ?present=1). A prospect opening a cold share link gets no Back button.
  const presenting =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('present') === '1';

  // Present opens this in a NEW tab, so there's no history to go back to —
  // closing the tab is what returns the rep to the account page. window.close()
  // works for script-opened tabs; if it's blocked (popup-blocked same-tab
  // fallback), navigate back instead.
  function handleBack() {
    window.close();
    window.setTimeout(() => window.history.back(), 120);
  }

  function handleCopyLink() {
    void navigator.clipboard
      ?.writeText(window.location.href)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  }

  if (looksLikePricingProposal(decoded) && !proposalData) {
    return (
      <div className="se-page se-shared">
        <div className="se-brandhead" style={{ display: 'flex' }}>
          <MoxoLogo size={30} />
          <span className="se-brandhead__label">Proposal</span>
        </div>
        <div className="se-note se-note--error" role="alert">
          This proposal link is invalid or incomplete. Ask the sender for a fresh link.
        </div>
      </div>
    );
  }

  if (proposalData) {
    return (
      <div className="se-page se-shared">
        <div className="se-shared__bar no-print">
          <div className="se-brandhead" style={{ display: 'flex', margin: 0, border: 0, padding: 0 }}>
            <MoxoLogo size={28} />
            <span className="se-brandhead__label">Proposal</span>
          </div>
          <div className="se-shared__actions">
            {presenting && (
              <button type="button" className="se-action" onClick={handleBack}>
                ← Back
              </button>
            )}
            <button type="button" className="se-action" onClick={handleCopyLink}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button type="button" className="se-action" onClick={() => window.print()}>
              Export PDF
            </button>
          </div>
        </div>
        <ProposalDeck proposal={proposalData} showActions={false} />
      </div>
    );
  }

  if (looksLikeCall(decoded) && !callData) {
    return (
      <div className="se-page se-shared">
        <div className="se-brandhead" style={{ display: 'flex' }}>
          <MoxoLogo size={30} />
          <span className="se-brandhead__label">Deal Brief</span>
        </div>
        <div className="se-note se-note--error" role="alert">
          This link is invalid or incomplete. Ask the sender for a fresh link.
        </div>
      </div>
    );
  }

  if (callData && callData.brief) {
    return (
      <div className="se-page se-shared">
        <div className="se-shared__bar no-print">
          <div className="se-brandhead" style={{ display: 'flex', margin: 0, border: 0, padding: 0 }}>
            <MoxoLogo size={28} />
            <span className="se-brandhead__label">Deal Brief</span>
          </div>
          <div className="se-shared__actions">
            {presenting && (
              <button type="button" className="se-action" onClick={handleBack}>
                ← Back
              </button>
            )}
            <button type="button" className="se-action" onClick={handleCopyLink}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button type="button" className="se-action" onClick={() => window.print()}>
              Export PDF
            </button>
          </div>
        </div>
        <CallBriefView result={callData} showActions={false} />
      </div>
    );
  }

  if (looksLikeBrief(decoded) && !briefData) {
    return (
      <div className="se-page se-shared">
        <div className="se-brandhead" style={{ display: 'flex' }}>
          <MoxoLogo size={30} />
          <span className="se-brandhead__label">Executive Brief</span>
        </div>
        <div className="se-note se-note--error" role="alert">
          This brief link is invalid. Ask the sender for a new link.
        </div>
      </div>
    );
  }

  if (briefData && briefData.brief) {
    return (
      <div className="se-page se-shared">
        <div className="se-shared__bar no-print">
          <div className="se-brandhead" style={{ display: 'flex', margin: 0, border: 0, padding: 0 }}>
            <MoxoLogo size={28} />
            <span className="se-brandhead__label">Executive Brief</span>
          </div>
          <div className="se-shared__actions">
            {presenting && (
              <button type="button" className="se-action" onClick={handleBack}>
                ← Back
              </button>
            )}
            <button type="button" className="se-action" onClick={handleCopyLink}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button type="button" className="se-action" onClick={() => window.print()}>
              Export PDF
            </button>
          </div>
        </div>
        <ExecutiveBriefView
          result={briefData}
          processName={briefData.processName || 'Use case'}
          showSources={false}
          showActions={false}
        />
      </div>
    );
  }

  if (!data || !data.report) {
    return (
      <div className="se-page se-shared">
        <div className="se-brandhead" style={{ display: 'flex' }}>
          <MoxoLogo size={30} />
          <span className="se-brandhead__label">Discovery Report</span>
        </div>
        <div className="se-note se-note--error" role="alert">
          This shared report link is invalid or incomplete. Ask the sender for a fresh link.
        </div>
      </div>
    );
  }

  const report = data.report;
  const processName = data.processName || 'Discovery';
  const totalLeakMinutes = report.processSteps.reduce(
    (sum, s) => sum + (Number.isFinite(s.leakMinutes) ? s.leakMinutes : 0),
    0,
  );
  const stepCount = report.processSteps.length;

  return (
    <div className="se-page se-shared">
      <div className="se-shared__bar no-print">
        <div className="se-brandhead" style={{ display: 'flex', margin: 0, border: 0, padding: 0 }}>
          <MoxoLogo size={28} />
          <span className="se-brandhead__label">Discovery Report</span>
        </div>
        <div className="se-shared__actions">
          {presenting && (
            <button type="button" className="se-action" onClick={handleBack}>
              ← Back
            </button>
          )}
          <button type="button" className="se-action" onClick={handleCopyLink}>
            {copied ? 'Link copied' : 'Copy link'}
          </button>
          <button type="button" className="se-action" onClick={() => window.print()}>
            Export PDF
          </button>
        </div>
      </div>

      <article className="se-report">
        <div className="se-brandhead print-share-only">
          <MoxoLogo size={30} />
          <span className="se-brandhead__label">Discovery Report</span>
        </div>

        <ReportHero
          processName={processName}
          companyName={data.companyName}
          enrichment={
            data.enrichment ?? {
              status: 'error',
              domain: null,
              industry: null,
              size: null,
              founded: null,
              hqLocation: null,
              homepageFetched: false,
            }
          }
          industryLabel={report.industryLabel}
          companySummary={report.companySummary}
        />

        <ProcessSequence steps={report.processSteps} />

        <TransformationShift
          challenges={report.challenges}
          solutions={report.moxoSolutions}
          totalLeakMinutes={totalLeakMinutes}
          stepCount={stepCount}
        />

        {data.roiDefaults && (
          <RoiCalculator
            roiDefaults={data.roiDefaults}
            totalLeakMinutesPerRun={totalLeakMinutes}
            stepCount={stepCount}
          />
        )}
      </article>
    </div>
  );
}
